import { useState } from 'react';
import { FiChevronDown, FiMenu, FiX } from 'react-icons/fi';

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: 'https://www.iheart-hc.com/' },
  {
    label: 'About',
    href: 'https://www.iheart-hc.com/about',
    children: [
      { label: 'Our Mission & History', href: 'https://www.iheart-hc.com/our-mission' },
      { label: 'Proven Performance', href: 'https://www.iheart-hc.com/general-5' },
      { label: 'Where We Operate', href: 'https://www.iheart-hc.com/location-1' },
      { label: 'HIPAA Compliance', href: 'https://www.iheart-hc.com/hipaa-compliance' },
      { label: 'Blog', href: 'https://www.iheart-hc.com/blog' },
    ],
  },
  {
    label: 'Core Systems',
    href: 'https://www.iheart-hc.com/services',
    children: [
      { label: 'Home Care Scheduling', href: 'https://www.iheart-hc.com/home-care-scheduling' },
      { label: 'Home Care On Call Service', href: 'https://www.iheart-hc.com/home-care-on-call-services' },
      { label: 'Home Care Recruiting', href: 'https://www.iheart-hc.com/home-care-recruiting' },
      { label: 'Home Care Virtual Assistants', href: 'https://www.iheart-hc.com/home-care-virtual-assistants' },
    ],
  },
  {
    label: 'Contact',
    href: 'https://www.iheart-hc.com/contact-us',
    children: [
      { label: 'Intake Form', href: 'https://www.iheart-hc.com/intake-form' },
      { label: 'Caregiver: Apply Here', href: 'https://www.iheart-hc.com/caregiver-apply-here' },
      { label: 'Careers', href: 'https://www.iheart-hc.com/careers' },
    ],
  },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState<string | null>(null);

  return (
    <header className="relative z-50 bg-white">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-12">
            <a href="https://www.iheart-hc.com/" className="flex items-center flex-shrink-0">
              <img
                src="/assets/logo.png"
                alt="Home Care Admin Experts"
                className="h-[120px] w-auto object-contain"
              />
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-16 font-montserrat">
              {NAV_ITEMS.map((item) => (
                <div key={item.label} className="relative group py-9 -my-9">
                  <a
                    href={item.href}
                    onClick={(e) => {
                      if (item.label === 'About') e.preventDefault();
                    }}
                    className="text-base tracking-[0.05em] text-black group-hover:text-ihc-plum transition-colors duration-150"
                  >
                    {item.label}
                  </a>

                  {item.children && (
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 absolute left-1/2 -translate-x-1/2 top-full z-50">
                      <div className="bg-white py-4 px-2 grid grid-flow-col grid-rows-2 gap-x-8 gap-y-1 whitespace-nowrap">
                        {item.children.map((child) => (
                          <a
                            key={child.label}
                            href={child.href}
                            className="block px-4 py-2 text-base text-center tracking-[0.05em] text-black hover:text-ihc-plum transition-colors"
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <a
                href="https://www.iheart-hc.com/partners"
                className="text-base tracking-[0.05em] text-black hover:text-ihc-plum transition-colors duration-150"
              >
                Partners
              </a>
            </nav>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-black hover:text-ihc-plum focus:outline-none"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          >
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="lg:hidden border-t border-black/5 bg-white px-5 py-4 space-y-1 font-montserrat">
          {NAV_ITEMS.map((item) => (
            <div key={item.label}>
              <div className="flex items-center justify-between">
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.label === 'About') e.preventDefault();
                  }}
                  className="flex-1 py-2.5 text-base tracking-[0.05em] text-black"
                >
                  {item.label}
                </a>
                {item.children && (
                  <button
                    type="button"
                    onClick={() =>
                      setMobileSubOpen((v) => (v === item.label ? null : item.label))
                    }
                    className="p-2.5 text-black"
                    aria-label={`Toggle ${item.label} submenu`}
                  >
                    <FiChevronDown
                      size={16}
                      className={`transition-transform ${mobileSubOpen === item.label ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
              </div>
              {item.children && mobileSubOpen === item.label && (
                <div className="pl-4 pb-2 space-y-1">
                  {item.children.map((child) => (
                    <a
                      key={child.label}
                      href={child.href}
                      className="block py-2 text-sm tracking-[0.05em] text-black hover:text-ihc-plum transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href="https://www.iheart-hc.com/partners"
            onClick={() => setMobileOpen(false)}
            className="block py-2.5 text-base tracking-[0.05em] text-black hover:text-ihc-plum transition-colors"
          >
            Partners
          </a>
        </nav>
      )}
    </header>
  );
}
